import { StandardEvents } from '@shopify/events';
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { fetchConfig, requestIdleCallback } from '@theme/utilities';

/** @typedef {import('@shopify/events').CartLinesUpdateEvent} CartLinesUpdateEvent */
/** @typedef {import('@shopify/events').CartAttributesUpdateEvent} CartAttributesUpdateEvent */

/** Identifies this component's own updates in event detail so it can skip them on rebound. */
const SOURCE = 'ibd-cart-attributes';

/**
 * The cart attributes managed by this component, mirroring the hidden inputs rendered by
 * snippets/ibd-cart-attributes.liquid:
 * - 'Consignment Date': the consignment date ('%B %d, %Y') or 'Ready for Dispatch'
 * - 'DaysToConsignment': the number of days to consignment
 * - 'HasPreOrder': 'true' or 'false'
 * - 'Discount $': the discount amount, with the '$', without the trailing zeros
 * - 'Discount %': the discount percentage, without the '%' sign
 * - 'TotalPreDiscount': the total pre-discount amount, with the '$', without the trailing zeros
 * - 'TotalPostDiscount': the total post-discount amount, with the '$', without the trailing zeros
 * @type {Record<string, string>}
 */
const EMPTY_ATTRIBUTES = {
  'Consignment Date': '',
  'DaysToConsignment': '',
  'HasPreOrder': '',
  'Discount $': '',
  'Discount %': '',
  'TotalPreDiscount': '',
  'TotalPostDiscount': '',
};

class IbdCartAttributes extends Component {
  /** @type {Record<string, string>} */
  cartAttributes = {};

  /** @type {AbortController | null} */
  #activeUpdate = null;

  /**
   * Separate from `#activeUpdate`. Emptying the cart morphs this element away before the
   * line-update promise settles, and `disconnectedCallback` aborts `#activeUpdate`.
   * The clear still has to reach the server after that.
   * @type {AbortController | null}
   */
  #clearUpdate = null;

  /** Attribute writes in flight on this instance. Foreign updates abort only these, not a clear. */
  #writeDepth = 0;

  /** Bumps on every foreign lines-update so an in-flight attribute write can see it. */
  #lineGeneration = 0;

  /** Latest lines-update generation whose promise resolved. A later rejection does not clear it. */
  #lineSucceededGeneration = 0;

  /** @type {Promise<unknown> | null} */
  #pendingLine = null;

  /** @type {Array<(generation: number) => void>} */
  #lineAborts = [];

  /** @type {CartAttributesUpdateEvent['promise'] | null} */
  #foreignAttributes = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(StandardEvents.cartAttributesUpdate, this.#onAttributesUpdate);
    document.addEventListener(ThemeEvents.cartSectionRestored, this.#onCartSectionRestored);

    // Defer the initial sync so it doesn't compete with page-load work.
    requestIdleCallback(() => {
      if (this.isConnected) void this.#init();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(StandardEvents.cartAttributesUpdate, this.#onAttributesUpdate);
    document.removeEventListener(ThemeEvents.cartSectionRestored, this.#onCartSectionRestored);

    this.#activeUpdate?.abort();
    this.#activeUpdate = null;
    // The sessionStorage cache is intentionally kept: cart drawer re-renders replace this
    // node, and clearing here would force a cart refetch on every re-render.
  }

  /**
   * Creates an abort controller. If an active update is found, it is aborted.
   * @returns {AbortController} The abort controller.
   */
  #createAbortController() {
    this.#activeUpdate?.abort();

    const abortController = new AbortController();
    this.#activeUpdate = abortController;
    return abortController;
  }

  /**
   * Initializes the cart attributes: hydrates the local cache (from sessionStorage or the
   * cart), then pushes the attributes rendered in the HTML if they differ.
   * @returns {Promise<void>}
   */
  #init = async () => {
    const storedAttributes = this.#retrieveStoredAttributes();

    if (Object.keys(storedAttributes).length > 0) {
      this.cartAttributes = storedAttributes;
    } else {
      try {
        const cart = await this.#getCart();
        this.cartAttributes = cart?.attributes ?? {};
        this.#storeAttributes(this.cartAttributes);
      } catch (error) {
        console.error('IBD | Cart Attributes | #init | error', error);
      }
    }

    const newAttributes = this.#getCartAttributesFromHtml(document);

    if (!Object.keys(newAttributes).length) return;
    if (this.#hasSameAttributes(this.cartAttributes, newAttributes)) return;

    await this.#updateCartAttributes(newAttributes);
  };

  /**
   * Resets the cart attributes.
   * @param {boolean} [clearCartAttributes=false] - Whether to also clear the attributes on the cart.
   * @returns {void}
   */
  #reset = (clearCartAttributes = false) => {
    this.cartAttributes = {};
    this.#clearStoredAttributes();

    if (clearCartAttributes) {
      void this.#clearCartAttributes();
    }
  };

  /**
   * Whether every attribute in `expected` already has the same value in `current`.
   * Only the keys in `expected` are compared: the cart may carry additional app-owned
   * attributes that this component doesn't manage.
   * @param {Record<string, string>} current
   * @param {Record<string, string>} expected
   * @returns {boolean}
   */
  #hasSameAttributes = (current, expected) => Object.entries(expected).every(([key, value]) => current[key] === value);

  /**
   * Gets the cart attributes from the html.
   * @param {Document} html - The html.
   * @returns {Record<string, string>} The cart attributes.
   */
  #getCartAttributesFromHtml = (html) => {
    const attributeInputs = html.querySelectorAll('ibd-cart-attributes input');

    /** @type {Record<string, string>} */
    const attributes = {};

    for (const input of attributeInputs) {
      if (!(input instanceof HTMLInputElement)) continue;

      const name = input.name?.match(/^attributes\[(.+)\]$/)?.[1];

      if (!name) continue;

      attributes[name] = input.value;
    }

    return attributes;
  };

  /**
   * Handles cart line updates (covers both add-to-cart and quantity/line changes).
   * @param {CartLinesUpdateEvent} event - The cart lines update event.
   */
  #onCartUpdate = (event) => {
    if (event.detail?.source === SOURCE) return;

    this.#lineGeneration += 1;
    const lineGeneration = this.#lineGeneration;
    const lineUpdate = event.promise ?? Promise.resolve(undefined);
    this.#pendingLine = lineUpdate;

    void lineUpdate.then(
      () => {
        if (lineGeneration > this.#lineSucceededGeneration) this.#lineSucceededGeneration = lineGeneration;
      },
      () => {
        // A rejected lines-update did not change the cart. An earlier success still stands.
      }
    );

    for (const abort of this.#lineAborts) abort(lineGeneration);

    void this.#finishCartUpdate(lineUpdate);
  };

  /**
   * @param {Promise<unknown>} lineUpdate
   */
  #finishCartUpdate = async (lineUpdate) => {
    /** @type {unknown} */
    let result;

    try {
      result = await lineUpdate;
    } catch (error) {
      if (!this.#isAbort(error)) {
        console.warn('IBD | Cart Attributes | #onCartUpdate | Event promise rejected:', error);
      }

      return;
    }

    if (!result || typeof result !== 'object') return;

    const detail = /** @type {{ detail?: { itemCount?: number, sections?: Record<string, string> } }} */ (result)
      .detail;

    if (detail?.itemCount === 0) {
      this.#reset(true);
      return;
    }

    const sections = detail?.sections;

    if (!sections || !Object.keys(sections).length) return;

    const updatedSectionHtml = this.#parseCartAttributesSection(sections);

    if (!updatedSectionHtml) {
      // The re-rendered cart section wasn't included in this event; attributes
      // will resync from the live DOM on the next #init.
      return;
    }

    const newAttributes = this.#getCartAttributesFromHtml(updatedSectionHtml);

    if (!Object.keys(newAttributes).length) return;
    if (this.#hasSameAttributes(this.cartAttributes, newAttributes)) return;

    await this.#updateCartAttributes(newAttributes);
  };

  /**
   * Re-syncs after `cart-items-component` re-renders the cart section on a back/forward
   * cache restore (Horizon 4.2.0). That render goes through `sectionRenderer` and never
   * dispatches `cartLinesUpdate`, so the hidden inputs are corrected without this component
   * hearing about it. Worse, on a Chrome disk-cache Back the page is rebuilt and `#init`
   * can read the stale inputs before the morph lands, pushing outdated attributes.
   * `#init` rehydrates from sessionStorage (current across pages), then compares against
   * the now-correct inputs and pushes only if they differ. The event bubbles from
   * `cart-items-component`, hence the document listener.
   */
  #onCartSectionRestored = () => {
    if (this.isConnected) void this.#init();
  };

  /**
   * Handles attribute updates initiated elsewhere (apps, agents, other theme code).
   * The event's `attributes` are the complete saved set. When its promise resolves
   * without `userErrors`, both cache copies are replaced with that set. A rejection
   * leaves them as they were. Updates initiated by this component are skipped.
   * An in-flight attribute write is aborted so it can re-read the cart and retry once.
   * @param {CartAttributesUpdateEvent} event - The cart attributes update event.
   */
  #onAttributesUpdate = (event) => {
    if (event.detail?.source === SOURCE) return;

    const attributes = Array.isArray(event.attributes) ? event.attributes : null;

    if (!attributes || !event.promise) return;

    this.#foreignAttributes = event.promise;

    if (this.#writeDepth > 0) this.#activeUpdate?.abort();

    void this.#replaceCacheFromEvent(event.promise, attributes);
  };

  /**
   * @param {CartAttributesUpdateEvent['promise']} promise
   * @param {CartAttributesUpdateEvent['attributes']} attributes
   */
  #replaceCacheFromEvent = async (promise, attributes) => {
    try {
      const result = await promise;

      if (result?.userErrors?.length) return;

      /** @type {Record<string, string>} */
      const next = {};

      for (const attribute of attributes) {
        if (typeof attribute?.key !== 'string') continue;

        next[attribute.key] = typeof attribute.value === 'string' ? attribute.value : '';
      }

      this.#replaceCache(next);
    } catch {
      // Rejected update: the cart is unchanged, nothing to sync.
    }
  };

  /**
   * Finds and parses the re-rendered section that contains the cart attribute inputs.
   * The cart attributes live inside the cart drawer / cart page section, so we scan
   * the event's rendered sections rather than assuming a fixed section id.
   * @param {Record<string, string>} sections - Map of section id to rendered HTML.
   * @returns {Document|null} The parsed section document, or null if none contained the inputs.
   */
  #parseCartAttributesSection = (sections) => {
    const parser = new DOMParser();

    for (const sectionHtml of Object.values(sections)) {
      if (!sectionHtml) continue;

      const parsed = parser.parseFromString(sectionHtml, 'text/html');

      if (parsed.querySelector('ibd-cart-attributes input')) return parsed;
    }

    return null;
  };

  /**
   * Writes this component's attributes without removing keys owned by apps.
   *
   * `Shopify.actions.updateCart` replaces the cart's whole attribute set, so the payload
   * is a fresh `/cart.js` read plus these keys. Neither cache copy is that list: both can
   * be behind the cart. After a successful write, both copies become the set we submitted.
   * The action emits `shopify:cart:attributes-update` itself.
   *
   * Between the `/cart.js` read and `updateCart` settling:
   * - A foreign `attributes-update` aborts the in-flight `updateCart`. This waits for that
   *   event's promise, reads `/cart.js` again, and calls `updateCart` once more. It does
   *   not retry a second time. Aborting does not roll back a request the server already
   *   accepted, so a stale set can still land after the other write.
   * - A foreign `lines-update` is awaited. If it rejects, the lines did not change and the
   *   set already built is sent. If it resolves, this payload is abandoned: it describes
   *   the previous cart. `#onCartUpdate` starts a new write when the event includes
   *   section HTML, and leaves the existing attributes in place when it does not.
   *
   * `userErrors` means the cart declined the change and is unchanged, so there is no AJAX
   * fallback. `#postCartAttributes` runs only when `updateCart` is missing or throws
   * (including "Online Store channel is locked"). An `AbortError` does neither.
   *
   * @param {Record<string, string>} newAttributes - The attributes this component computed.
   */
  #updateCartAttributes = async (newAttributes) => {
    if (!Object.keys(newAttributes).length) return;

    this.#writeDepth += 1;

    try {
      await this.#writeAttributes(newAttributes, false);
    } catch (error) {
      if (!this.#isAbort(error)) {
        console.error('IBD | Cart Attributes | #updateCartAttributes | error', error);
      }
    } finally {
      this.#writeDepth -= 1;
    }
  };

  /**
   * @param {Record<string, string>} newAttributes
   * @param {boolean} hasRetried - True when this is the one retry after a foreign attributes update.
   */
  #writeAttributes = async (newAttributes, hasRetried) => {
    const abortController = this.#createAbortController();
    const signal = abortController.signal;
    const lineGeneration = this.#lineGeneration;
    const foreignBefore = this.#foreignAttributes;
    /** @type {Record<string, string> | null} */
    let complete = null;
    let aborted = false;

    const lineAbort = this.#armLineAbort(lineGeneration, abortController);

    try {
      const cart = await this.#getCart(signal);

      if (await this.#linesChangedSince(lineGeneration)) return;
      if (this.#superseded(abortController)) return;

      if (this.#foreignAttributes !== foreignBefore) {
        await this.#retryAfterForeignAttributes(newAttributes, hasRetried, abortController, lineGeneration);
        return;
      }

      complete = this.#mergeAttributes(cart?.attributes, newAttributes);
      await this.#commitAttributeSet(complete, newAttributes, signal);
    } catch (error) {
      if (!this.#isAbort(error)) throw error;

      aborted = true;
    } finally {
      this.#disarmLineAbort(lineAbort);
    }

    if (!aborted) return;

    await this.#recoverAbortedWrite({
      newAttributes,
      hasRetried,
      abortController,
      lineGeneration,
      foreignBefore,
      signal,
      complete,
    });
  };

  /**
   * @param {{
   *   newAttributes: Record<string, string>,
   *   hasRetried: boolean,
   *   abortController: AbortController,
   *   lineGeneration: number,
   *   foreignBefore: CartAttributesUpdateEvent['promise'] | null,
   *   signal: AbortSignal,
   *   complete: Record<string, string> | null,
   * }} recovery
   */
  #recoverAbortedWrite = async (recovery) => {
    const { newAttributes, hasRetried, abortController, lineGeneration, foreignBefore, signal, complete } = recovery;

    if (this.#superseded(abortController)) return;
    if (await this.#linesChangedSince(lineGeneration)) return;
    if (this.#superseded(abortController)) return;

    if (this.#foreignAttributes !== foreignBefore) {
      await this.#retryAfterForeignAttributes(newAttributes, hasRetried, abortController, lineGeneration);
      return;
    }

    if (!signal.aborted) return;

    // The lines did not change. Send the set we already built, without another /cart.js read.
    if (complete) {
      await this.#resendBuiltSet(complete, newAttributes, lineGeneration, hasRetried, foreignBefore);
      return;
    }

    // The read itself was aborted and the lines did not change, so read again.
    if (this.#lineGeneration !== lineGeneration) {
      await this.#writeAttributes(newAttributes, hasRetried);
    }
  };

  /**
   * @param {Record<string, string>} complete
   * @param {Record<string, string>} newAttributes
   * @param {number} lineGeneration
   * @param {boolean} hasRetried
   * @param {CartAttributesUpdateEvent['promise'] | null} foreignBefore
   */
  #resendBuiltSet = async (complete, newAttributes, lineGeneration, hasRetried, foreignBefore) => {
    const abortController = this.#createAbortController();
    const resendGeneration = this.#lineGeneration;
    const lineAbort = this.#armLineAbort(resendGeneration, abortController);

    try {
      await this.#commitAttributeSet(complete, newAttributes, abortController.signal);
    } catch (error) {
      if (!this.#isAbort(error)) throw error;
      if (this.#superseded(abortController)) return;
      if (await this.#linesChangedSince(resendGeneration)) return;

      if (this.#foreignAttributes !== foreignBefore) {
        await this.#retryAfterForeignAttributes(newAttributes, hasRetried, abortController, lineGeneration);
      }
    } finally {
      this.#disarmLineAbort(lineAbort);
    }
  };

  /**
   * @param {Record<string, string>} newAttributes
   * @param {boolean} hasRetried
   * @param {AbortController} abortController
   * @param {number} lineGeneration
   */
  #retryAfterForeignAttributes = async (newAttributes, hasRetried, abortController, lineGeneration) => {
    if (hasRetried) return;

    const foreign = this.#foreignAttributes;

    if (!foreign) return;

    try {
      await foreign;
    } catch {
      // The cart was not changed.
    }

    if (this.#superseded(abortController)) return;
    if (await this.#linesChangedSince(lineGeneration)) return;

    await this.#writeAttributes(newAttributes, true);
  };

  /**
   * @param {Record<string, string>} complete - Every attribute key that should remain on the cart.
   * @param {Record<string, string>} newAttributes - This component's keys; the AJAX fallback merges these.
   * @param {AbortSignal} signal
   */
  #commitAttributeSet = async (complete, newAttributes, signal) => {
    const updateCart = window.Shopify?.actions?.updateCart;

    if (typeof updateCart !== 'function') {
      await this.#postCartAttributes(newAttributes, signal);
      this.#replaceCache(complete);
      return;
    }

    try {
      const { userErrors, warnings } = await updateCart(
        { attributes: this.#attributeEntries(complete) },
        { signal, event: { detail: { source: SOURCE } } }
      );

      if (userErrors?.length) {
        console.error(
          'IBD | Cart Attributes | #updateCartAttributes | error',
          userErrors.map((userError) => userError.message).join('; ')
        );
        return;
      }

      if (warnings?.length) {
        console.warn('IBD | Cart Attributes | #updateCartAttributes | warnings', warnings);
      }

      this.#replaceCache(complete);
    } catch (error) {
      if (this.#isAbort(error) || signal.aborted) throw error;

      await this.#postCartAttributes(newAttributes, signal);
      this.#replaceCache(complete);
    }
  };

  /**
   * Aborts `controller` when a foreign lines-update starts after `generation`.
   * @param {number} generation
   * @param {AbortController} controller
   */
  #armLineAbort = (generation, controller) => {
    /** @type {(current: number) => void} */
    const abort = (current) => {
      if (current !== generation) controller.abort();
    };

    this.#lineAborts.push(abort);
    abort(this.#lineGeneration);
    return abort;
  };

  /**
   * @param {(generation: number) => void} abort
   */
  #disarmLineAbort = (abort) => {
    this.#lineAborts = this.#lineAborts.filter((entry) => entry !== abort);
  };

  /**
   * True when the lines-update that started after `generation` resolved.
   * A rejection means the lines did not change.
   * @param {number} generation
   * @returns {Promise<boolean>}
   */
  #linesChangedSince = async (generation) => {
    if (this.#lineGeneration === generation) return false;

    const pending = this.#pendingLine;

    if (pending) {
      try {
        await pending;
      } catch {
        // The latest lines-update did not change the cart.
      }
    }

    // Any resolved lines-update since `generation` changed the cart, even when a later one failed.
    return this.#lineSucceededGeneration > generation;
  };

  /**
   * @param {AbortController} abortController
   * @returns {boolean}
   */
  #superseded = (abortController) => this.#activeUpdate !== abortController;

  /**
   * @param {unknown} attributes
   * @param {Record<string, string>} updates
   * @returns {Record<string, string>}
   */
  #mergeAttributes = (attributes, updates) => {
    /** @type {Record<string, string>} */
    const merged = {};

    if (attributes && typeof attributes === 'object') {
      for (const [key, value] of Object.entries(attributes)) {
        merged[key] = typeof value === 'string' ? value : '';
      }
    }

    for (const [key, value] of Object.entries(updates)) {
      merged[key] = value;
    }

    return merged;
  };

  /**
   * @param {Record<string, string>} attributes
   */
  #attributeEntries = (attributes) => Object.entries(attributes).map(([key, value]) => ({ key, value }));

  /**
   * @param {unknown} error
   * @returns {boolean}
   */
  #isAbort = (error) => error instanceof Error && error.name === 'AbortError';

  /**
   * Clears the managed attributes on the cart (used when the cart empties).
   *
   * Stays on the AJAX API: an empty string removes a key there, and this call names only
   * this component's keys. `updateCart` would have to send every key we want to keep, and
   * an empty value is not documented to remove a key.
   *
   * Uses its own abort controller. The cart section morph removes this element before the
   * line-update promise settles, and `disconnectedCallback` aborts the attribute write.
   * This clear still has to run after that.
   */
  #clearCartAttributes = async () => {
    this.#clearUpdate?.abort();

    const abortController = new AbortController();
    this.#clearUpdate = abortController;

    try {
      await this.#postCartAttributes(EMPTY_ATTRIBUTES, abortController.signal);
    } catch (error) {
      if (!this.#isAbort(error)) {
        console.error('IBD | Cart Attributes | #clearCartAttributes | error', error);
      }
    } finally {
      if (this.#clearUpdate === abortController) {
        this.#clearUpdate = null;
      }
    }
  };

  /**
   * Posts attributes directly to the AJAX cart update endpoint.
   * A partial `attributes` object is merged; an empty string removes that key.
   * @param {Record<string, string>} attributes - The attributes to set.
   * @param {AbortSignal} signal - Abort signal for the request.
   */
  #postCartAttributes = async (attributes, signal) => {
    const config = fetchConfig('json', {
      body: JSON.stringify({ attributes }),
    });

    const response = await fetch(Theme.routes.cart_update_url, { ...config, signal });

    if (!response.ok) {
      throw new Error('Failed to update cart attributes');
    }

    return response.json();
  };

  /**
   * Gets the cart from the AJAX API. The AJAX response includes `attributes`, which the
   * standard `getCart` action does not return.
   * @param {AbortSignal} [signal]
   */
  #getCart = async (signal) => {
    const response = await fetch(`${Theme.routes.cart_url}.js`, signal ? { signal } : undefined);

    if (!response.ok) {
      throw new Error('Failed to get cart');
    }

    return await response.json();
  };

  /**
   * Replaces both cache copies with the complete attribute set. Does not merge.
   * @param {Record<string, string>} attributes
   */
  #replaceCache = (attributes) => {
    this.cartAttributes = { ...attributes };

    if (!Object.keys(this.cartAttributes).length) {
      this.#clearStoredAttributes();
      return;
    }

    this.#storeAttributes(this.cartAttributes);
  };

  /**
   * Stores the cart attributes in the session storage.
   * @param {Record<string, string>} attributes - The attributes to store.
   * @returns {void}
   */
  #storeAttributes = (attributes) => {
    if (!Object.keys(attributes).length) return;

    try {
      sessionStorage.setItem(`ibd-cart-attributes-${Shopify.country}`, JSON.stringify(attributes));
    } catch (error) {
      console.error('IBD | Cart Attributes | storeAttributes | error', error);
    }
  };

  /**
   * Retrieves the cart attributes from the session storage.
   * @returns {Record<string, string>} The attributes, or an empty object if not found or an error occurs.
   */
  #retrieveStoredAttributes = () => {
    const attributes = sessionStorage.getItem(`ibd-cart-attributes-${Shopify.country}`);

    if (!attributes) return {};

    try {
      return JSON.parse(attributes);
    } catch (error) {
      console.error('IBD | Cart Attributes | retrieveStoredAttributes | error', error);
      return {};
    }
  };

  /**
   * Clears the stored attributes from the session storage.
   * @returns {void}
   */
  #clearStoredAttributes = () => {
    sessionStorage.removeItem(`ibd-cart-attributes-${Shopify.country}`);
  };
}

if (!customElements.get('ibd-cart-attributes')) {
  customElements.define('ibd-cart-attributes', IbdCartAttributes);
}
